class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setLoading(isLoading) {
    this.classList.toggle("is-loading", isLoading);
    this.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  preserveCrossSell() {
    const container = this.querySelector("[data-cart-cross-sell]");

    if (!container || !container.innerHTML.trim()) return null;

    return {
      html: container.innerHTML,
      loaded: container.dataset.loaded === "true",
    };
  }

  restoreCrossSell(savedCrossSell) {
    if (!savedCrossSell) return false;

    const container = this.querySelector("[data-cart-cross-sell]");
    if (!container) return false;

    container.innerHTML = savedCrossSell.html;
    container.dataset.loaded = savedCrossSell.loaded ? "true" : "false";
    container.dataset.loading = "false";

    return true;
  }

  loadCrossSell() {
    const container = this.querySelector("[data-cart-cross-sell]");

    if (!container || container.dataset.loaded === "true") return;

    const sectionUrl = container.dataset.cartCrossSellUrl;
    if (!sectionUrl) return;

    const injectCrossSell = (html) => {
      const currentContainer = this.querySelector("[data-cart-cross-sell]");
      if (!currentContainer || currentContainer.dataset.loaded === "true") return;

      currentContainer.innerHTML = html;
      currentContainer.dataset.loaded = "true";
      currentContainer.dataset.loading = "false";

      currentContainer.querySelectorAll("[data-aos]").forEach((element) => {
        element.classList.add("aos-animate");
      });
    };

    if (window.__stutiCartCrossSellHTML) {
      injectCrossSell(window.__stutiCartCrossSellHTML);
      return;
    }

    container.dataset.loading = "true";

    if (!window.__stutiCartCrossSellPromise) {
      window.__stutiCartCrossSellPromise = fetch(sectionUrl, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Unable to load cart cross-sell: ${response.status}`);
          }

          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(
            html,
            "text/html"
          );
          const section = documentFragment.querySelector(".shopify-section");
          const sectionHTML = section ? section.innerHTML : html;

          window.__stutiCartCrossSellHTML = sectionHTML;
          return sectionHTML;
        })
        .catch((error) => {
          window.__stutiCartCrossSellPromise = null;
          throw error;
        });
    }

    window.__stutiCartCrossSellPromise
      .then(injectCrossSell)
      .catch((error) => {
        const currentContainer = this.querySelector("[data-cart-cross-sell]");
        if (currentContainer) currentContainer.dataset.loading = "false";
        console.error(error);
      });
  }

  open(triggeredBy) {
    const isCartPage = document.querySelector("cart-items");

    if (isCartPage) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);

    requestAnimationFrame(() => {
      this.classList.add("animate", "active");
      this.loadCrossSell();
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer-inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer-inner") ||
          this.querySelector(".drawer-close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("cart-drawer-quick-view");
   if (window.matchMedia("(min-width: 768px)").matches) {
    document.body.style.overflow = "hidden";
    document.body.style.padding = "0 4px 0 0";
   }

    const animatedElements = this.querySelectorAll("[data-aos]");
    animatedElements.forEach((element) => {
      element.classList.add("aos-animate");
    });
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("cart-drawer-quick-view");
    if (window.matchMedia("(min-width: 768px)").matches) {
    document.body.style.overflow = "scroll";
    document.body.style.padding = "0";
    document.body.style.overflowX = "hidden";
    }

    const animatedElements = this.querySelectorAll(".aos-animate");
    animatedElements.forEach((element) => {
      element.classList.remove("aos-animate");
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const savedCrossSell = this.preserveCrossSell();

    this.querySelector(".drawer-inner").classList.contains("is-empty") &&
      this.querySelector(".drawer-inner").classList.remove("is-empty");

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.restoreCrossSell(savedCrossSell);
    this.setLoading(false);

    requestAnimationFrame(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer-inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
